import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { addToCart } from "@app/scripts/utils";
import { PersonalisationTemplate, XY } from "@app/scripts/types";
import config from "@app/config";

type Props = {
  personalisationState: boolean;
  selectedVariantId?: number;
  productTags: string[];
  closePersonalisation: VoidFunction;
  personalisationPos: XY;
  mainImage: string | null;
  template: PersonalisationTemplate | null;
};

const lookupTable = config.stores.presonalisationIds;

export const PersonalisationDrawer: React.FC<Props> = ({
  personalisationState,
  selectedVariantId,
  productTags = [],
  closePersonalisation,
  personalisationPos,
  mainImage,
}) => {
  const [customText, setCustomText] = useState("");
  const [selectedFont, setSelectedFont] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const isPen = productTags.includes("Pen Personalisation");
  const allOptionsSelected = isPen ? selectedFont : selectedColor && customText.length;

  const maxChars = isPen ? 3 : 6;

  useEffect(() => {
    if (personalisationState) {
      setIsActive(true);
    }
  }, [personalisationState]);

  const handleFontSelect = (event: any) => {
    setSelectedFont(event.target.value);
  };

  const handleColorSelect = (event: any) => {
    setSelectedColor(event.target.value);
  };

  const handleTextChange = (event: any) => {
    setCustomText(event.target.value);
  };

  const addToBag = async () => {
    setIsLoading(true);

    const properties: any = {};
    if (customText) properties["Personalisation Text"] = customText;
    if (selectedFont) properties["Personalisation Font"] = selectedFont;
    if (selectedColor) properties["Personalisation Colour"] = selectedColor;

    try {
      if (!selectedVariantId) throw "No variant id received";

      await addToCart({
        id: selectedVariantId,
        qty: 1,
        properties,
      });

      let personalisationId;

      if (isPen && properties["Personalisation Font"]) {
        personalisationId = lookupTable?.[properties["Personalisation Font"]];
      } else if (productTags.includes("Paper Personalisation") && properties["Personalisation Colour"]) {
        personalisationId = lookupTable?.[properties["Personalisation Colour"]];
      }

      if (personalisationId) {
        const personalisationData = {
          id: personalisationId,
          qty: 1,
          properties: undefined,
        };

        await addToCart(personalisationData);
      }
      handleClose();
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  };

  const handleClose = () => {
    setIsActive(false);
    setTimeout(() => {
      closePersonalisation();
    }, 200);
  };

  const ref = useRef<HTMLElement | null>(null);
  const [fontSize, setFontSize] = useState(12);

  const handleFontSize = useCallback(() => {
    if (!ref?.current) return;
    setFontSize(ref.current.getBoundingClientRect().width * 0.035);
  }, [ref?.current]);

  useEffect(() => {
    handleFontSize();

    window.addEventListener("resize", handleFontSize);

    return () => {
      window.removeEventListener("resize", handleFontSize);
    };
  }, [ref.current]);  


  const [locales, setLocales] = useState(null);

  useEffect(() => {
    const localesElement = document.getElementById("locales-json");
    if (localesElement) {
      try {
        const escapedJson = localesElement.getAttribute("data-json") || "{}";
        const localesData = JSON.parse(escapedJson);
        setLocales(localesData);
      } catch (error) {
      }
    }
  }, []);


  const defaultStrings = useMemo(
    () => ({
      title: "Personalisation",
      add_text: "Add your text:",
      select_font: "Select Font:",
      select_colour: "Select Colour:",
      processing:
        "Allow 5-7 business days for processing. Refunds will not be accepted on personalised products.",
      cancel: "CANCEL",
      loading: "Loading...",
      add_to_cart: "ADD TO CART",
    }),
    []
  );

  const strings = locales || defaultStrings;

  if (!strings) return null;

  
  return (
    <>
      <div className={`personalisation__overlay ${isActive ? "active" : "closing"}`} onClick={handleClose}></div>
      <div className={`personalisation ${isActive ? "active" : "closing"}`}>
        <div className="personalisation__heading">
          <span>{strings.title}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            onClick={handleClose}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.5848 0L14 1.41517L8.414 7L14 12.5848L12.5848 14L7 8.414L1.41517 14L0 12.5848L5.586 7L0 1.41517L1.41517 0L7 5.586L12.5848 0Z"
              fill="black"
            />
          </svg>
        </div>

        <div className="personalisation__slide">
          <div className="w-full" ref={ref}>
            <img src={mainImage || undefined} alt="Product" className="product-image" />
            <div
              id={`overlay-text-${isPen ? "pen" : "paper"}`}
              className={`overlay-text-${isPen ? "pen" : "paper"}`}
              style={{
                fontSize: `${fontSize}px`,
                fontFamily: selectedFont,
                color: selectedColor,
                left: personalisationPos?.x ? `${personalisationPos.x}%` : undefined,
                top: personalisationPos?.y ? `${personalisationPos.y}%` : undefined,
              }}
            >
              {customText || "Your Initials"}
            </div>
          </div>
        </div>

        <div className="personalisation__variants">
          <div className="personalisation-text">
            <span htmlFor="personalisation-text-input" className="personalisation-text-char">
              {strings.add_text} <span className="personalisation-text-char-limit">(Max {maxChars} characters)</span>
            </span>
            <input
              type="text"
              id="personalisation-text-input"
              className="w-full h-11 px-4 py-2 pr-12 border border-gray-300 rounded min-w-0"
              value={customText}
              onChange={handleTextChange}
              maxLength={maxChars}
            />
          </div>
        </div>

        <div className="personalisation__variants">
          <div className="font-options">
            {isPen ? (
              <>
                <span className="personalisation-text-char">
                  {strings.select_font + " "}
                  <span className="personalisation-text-char-limit" style={{ fontFamily: selectedFont }}>
                    {selectedFont}
                  </span>
                </span>
                <div className="color-swatches">
                  {["Baskerville", "Optima", "Gill Sans"].map((font) => (
                    <label
                      key={font}
                      className="mr-4"
                      style={{
                        border: selectedFont === font ? "1px solid black" : undefined, // Adds black border if selected
                        padding: "2px",
                        cursor: "pointer",
                      }}
                    >
                      <input type="radio" name="font" value={font} onChange={handleFontSelect} className="mr-2" />
                      <span style={{ fontFamily: font }}>{font}</span>
                    </label>
                  ))}
                </div>
              </>
            ) : (
              <>
                <span className="personalisation-text-char -mt-3">
                  {strings.select_colour} <span className="personalisation-text-char-limit">{selectedColor}</span>
                </span>
                <div className="color-swatches">
                  {["Gold", "Silver", "Blind"].map((color) => (
                    <label
                      key={color}
                      className="swatch"
                      style={{
                        border: selectedColor === color ? "2px solid black" : "1px solid transparent",
                        cursor: "pointer",
                        backgroundColor: color === "Gold" ? "#d4af37" : color === "Silver" ? "#C0C0C0" : "#000",
                        width: "38px",
                        height: "38px",
                      }}
                      onChange={handleColorSelect}
                    >
                      <input type="radio" name="color" value={color} style={{ display: "none" }} />
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="personalisation__variants -mt-6 pb-6">
          <div className="processing-notice">
            <p className="quick_add_desc">
              {strings.processing}
            </p>
          </div>
        </div>

        <div className="personalisation__cta">
          <span
            className="add-to-cart"
            style={{ color: "black", background: "white", border: "1px solid black" }}
            onClick={handleClose}
          >
            {strings.cancel}
          </span>
          <span
            onClick={isLoading ? undefined : addToBag}
            className="add-to-cart"
            style={{
              opacity: isLoading ? 0.6 : 1,
              pointerEvents: isLoading || !allOptionsSelected ? "none" : "auto",
              backgroundColor: !allOptionsSelected ? "grey" : "black",
            }}
          >
            {isLoading ? strings.loading : strings.add_to_cart}
          </span>
        </div>
      </div>
    </>
  );
};
